import { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Typography,
  LinearProgress,
  Skeleton,
  Alert,
} from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import SpeedIcon from "@mui/icons-material/Speed";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import StatCard from "@/components/StatCard";
import UsageChart from "@/components/UsageChart";
import RecentRendersTable from "@/components/RecentRendersTable";
import { getOverview } from "@/api/dashboard";
import type { OverviewData } from "@/api/dashboard";

export default function OverviewPage() {
  const [data, setData] = useState<OverviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getOverview()
      .then(setData)
      .catch((err) =>
        setError(err.response?.data?.error ?? "Failed to load overview"),
      )
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Box>
        <Typography variant="h5" component="h1" gutterBottom>
          Overview
        </Typography>
        <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={6} sm={6} md={3} key={i}>
              <Skeleton variant="rounded" height={120} />
            </Grid>
          ))}
          <Grid item xs={12}>
            <Skeleton variant="rounded" height={340} />
          </Grid>
          <Grid item xs={12}>
            <Skeleton variant="rounded" height={300} />
          </Grid>
        </Grid>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography variant="h5" component="h1" gutterBottom>
          Overview
        </Typography>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!data) return null;

  const { usage, recentRenders, dailyUsage } = data;
  const usagePercent =
    usage.rendersLimit > 0
      ? Math.round((usage.rendersUsed / usage.rendersLimit) * 100)
      : 0;

  const completedCount = recentRenders.filter(
    (r) => r.status === "completed",
  ).length;
  const timedRenders = recentRenders.filter((r) => r.renderTimeMs !== null);
  const avgRenderTime =
    timedRenders.reduce((sum, r) => sum + (r.renderTimeMs ?? 0), 0) /
    (timedRenders.length || 1);

  return (
    <Box>
      <Typography variant="h5" component="h1" gutterBottom>
        Overview
      </Typography>

      <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
        {/* Stat cards */}
        <Grid item xs={6} sm={6} md={3}>
          <StatCard
            title="Plan"
            value={usage.plan.charAt(0).toUpperCase() + usage.plan.slice(1)}
            subtitle={`${usage.billingCycle} billing`}
            icon={<TrendingUpIcon />}
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatCard
            title="Renders This Month"
            value={`${usage.rendersUsed.toLocaleString()} / ${usage.rendersLimit.toLocaleString()}`}
            subtitle={`${usagePercent}% of quota`}
            icon={<DescriptionIcon />}
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatCard
            title="Success Rate"
            value={
              recentRenders.length > 0
                ? `${Math.round((completedCount / recentRenders.length) * 100)}%`
                : "N/A"
            }
            subtitle={`${completedCount} of ${recentRenders.length} recent`}
            icon={<CheckCircleIcon />}
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatCard
            title="Avg Render Time"
            value={
              avgRenderTime > 0
                ? `${(avgRenderTime / 1000).toFixed(1)}s`
                : "N/A"
            }
            subtitle="Recent renders"
            icon={<SpeedIcon />}
          />
        </Grid>

        {/* Usage bar */}
        <Grid item xs={12}>
          <Box sx={{ px: 1 }}>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}
            >
              <Typography variant="body2" color="text.secondary">
                Monthly Quota
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {usage.rendersUsed.toLocaleString()} /{" "}
                {usage.rendersLimit.toLocaleString()}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={Math.min(usagePercent, 100)}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: "divider",
                "& .MuiLinearProgress-bar": {
                  borderRadius: 4,
                  background: (theme) =>
                    `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                },
              }}
            />
          </Box>
        </Grid>

        {/* Usage chart */}
        <Grid item xs={12}>
          <UsageChart data={dailyUsage} />
        </Grid>

        {/* Recent renders table */}
        <Grid item xs={12}>
          <RecentRendersTable rows={recentRenders} />
        </Grid>
      </Grid>
    </Box>
  );
}
